import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Box from '@mui/material/Box'
import AppBar from '@mui/material/AppBar'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import IconButton from '@mui/material/IconButton'
import SkipPrevIcon from '@mui/icons-material/SkipPrevious'
import SkipNextIcon from '@mui/icons-material/SkipNext'

import { EnergyTab, AddonsTab, BatteryInvertersTab, PanelsTab } from '../tabs'
import { selectSelectedTab, setSelectedTab } from '../../store/slices/uiSlice'

// TabPanel component
const TabPanel = ({ children, value, index, ...other }) => {
    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`quote-tabpanel-${index}`}
            aria-labelledby={`quote-tab-${index}`}
            sx={{ overflow: 'auto', flex: 1 }}
            {...other}
        >
            {value === index && children}
        </Box>
    )
}

// Accessibility props for tabs
const a11yProps = (index) => ({
    id: `quote-tab-${index}`,
    'aria-controls': `quote-tabpanel-${index}`
})

// Styles using CSS Grid
const getStyles = (showLeftPanel, showRightPanel) => ({
    layout: {
        display: 'grid',
        gridTemplateColumns: showLeftPanel && showRightPanel ? '600px 1fr'
                           : showLeftPanel ? '1fr auto'
                           : '1fr',
        gridTemplateRows: 'auto 1fr',
        gap: 2,
        height: 'calc(100vh - 80px)',
        p: 2
    },
    header: {
        gridColumn: '1 / -1'
    },
    leftPanel: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1
    },
    tabBar: {
        flexShrink: 0,
        position: 'sticky',
        top: 0,
        zIndex: 10,
        borderRadius: '4px 4px 0 0',
        bgcolor: '#6fcae7'
    },
    tabContent: {
        flex: 1,
        overflow: 'auto'
    },
    footer: {
        flexShrink: 0,
        borderTop: '1px solid #ddd',
        p: 2
    },
    rightPanel: {
        overflow: 'auto',
        bgcolor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1,
        p: 2
    },
    tab: {
        fontSize: '12px',
        minHeight: 40,
        textTransform: 'none'
    }
})

/**
 * QuoteLayout - Main layout component using CSS Grid
 *
 * @param {ReactNode} header - Header content (customer info)
 * @param {ReactNode} rightPanel - Right panel content (CalcResults)
 * @param {ReactNode} footer - Footer content below tabs (CustomerSiteList)
 * @param {boolean} showLeftPanel - Whether to show the left panel (default: true)
 * @param {boolean} showRightPanel - Whether to show the right panel (default: true)
 * @param {function} onToggleRightPanel - Callback to toggle right panel visibility
 */
const QuoteLayout = ({ header, rightPanel, footer, showLeftPanel = true, showRightPanel = true, onToggleRightPanel }) => {
    const dispatch = useDispatch()
    const selectedTab = useSelector(selectSelectedTab)
    const styles = getStyles(showLeftPanel, showRightPanel)

    const handleTabChange = (event, newValue) => {
        dispatch(setSelectedTab(newValue))
    }

    return (
        <Box sx={styles.layout}>
            {/* Header */}
            {header && (
                <Box sx={styles.header}>
                    {header}
                </Box>
            )}

            {/* Left Panel - Tabs */}
            {showLeftPanel && (
                <Box sx={styles.leftPanel}>
                    <AppBar position="static" sx={styles.tabBar}>
                        <Tabs
                            value={selectedTab}
                            onChange={handleTabChange}
                            variant="fullWidth"
                            textColor="inherit"
                            indicatorColor="secondary"
                        >
                            <Tab label="Energy" {...a11yProps(0)} sx={styles.tab} />
                            <Tab label="Addons" {...a11yProps(1)} sx={styles.tab} />
                            <Tab label="Battery / Inverters" {...a11yProps(2)} sx={styles.tab} />
                            <Tab label="Panels" {...a11yProps(3)} sx={styles.tab} />
                        </Tabs>
                    </AppBar>
                    <Box sx={styles.tabContent}>
                        <TabPanel value={selectedTab} index={0}>
                            <EnergyTab />
                        </TabPanel>
                        <TabPanel value={selectedTab} index={1}>
                            <AddonsTab />
                        </TabPanel>
                        <TabPanel value={selectedTab} index={2}>
                            <BatteryInvertersTab />
                        </TabPanel>
                        <TabPanel value={selectedTab} index={3}>
                            <PanelsTab />
                        </TabPanel>
                    </Box>
                    {/* Footer below tabs (CustomerSiteList) */}
                    {footer && (
                        <Box sx={styles.footer}>
                            {footer}
                        </Box>
                    )}
                </Box>
            )}

            {/* Right Panel Toggle + Content */}
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', height: '100%' }}>
                {/* Toggle button - always visible */}
                {onToggleRightPanel && (
                    <IconButton
                        onClick={onToggleRightPanel}
                        size="small"
                        title={showRightPanel ? "Hide Results Panel" : "Show Results Panel"}
                        sx={{
                            mt: 1,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            '&:hover': { bgcolor: 'action.hover' }
                        }}
                    >
                        {showRightPanel ? <SkipNextIcon /> : <SkipPrevIcon />}
                    </IconButton>
                )}
                {/* Right Panel Content */}
                {showRightPanel && rightPanel && (
                    <Box sx={{ ...styles.rightPanel, flex: 1, height: '100%', overflow: 'auto' }}>
                        {rightPanel}
                    </Box>
                )}
            </Box>
        </Box>
    )
}

export default QuoteLayout
